import GithubSlugger from "github-slugger";

export interface TocItem {
  id: string;
  text: string;
  level: 2 | 3;
}

/** True when body looks like raw HTML rather than Markdown. */
export function isHtmlBody(body: string): boolean {
  return /^\s*(<!--|<style|<div|<section|<article|<h[1-6]|<p[\s>])/i.test(body ?? "");
}

/**
 * Demote any in-content <h1> to <h2>. Resource detail pages already render
 * the article title in a page-level <h1>; a second <h1> in the body would
 * give the page multiple H1s (Semrush #104).
 */
export function demoteH1ToH2(body: string): string {
  if (!body) return body;
  return body
    .replace(/<h1(\s[^>]*)?>/gi, (_full, attrs) => `<h2${attrs ?? ""}>`)
    .replace(/<\/h1>/gi, "</h2>");
}

function stripTags(s: string): string {
  return s
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
}

/** Extract H2/H3 headings from an HTML body, slugging text consistently. */
export function extractHtmlHeadings(body: string): TocItem[] {
  if (!body) return [];
  const slugger = new GithubSlugger();
  const items: TocItem[] = [];
  const re = /<h([23])(?:\s[^>]*)?>([\s\S]*?)<\/h\1>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    const level = (m[1] === "2" ? 2 : 3) as 2 | 3;
    const text = stripTags(m[2]);
    if (!text) continue;
    items.push({ id: slugger.slug(text), text, level });
  }
  return items;
}

/**
 * Inject id="..." and a scroll-margin class onto each <h2>/<h3> in the body
 * using the same heading order/slugs returned by extractHtmlHeadings.
 */
export function injectHtmlHeadingIds(body: string, items: TocItem[]): string {
  if (!body || items.length === 0) return body;
  let i = 0;
  return body.replace(/<h([23])(\s[^>]*)?>/gi, (full, lvl, attrs) => {
    const item = items[i++];
    if (!item) return full;
    const existing = attrs ?? "";
    if (/\sid\s*=/.test(existing)) return full;
    const classMatch = existing.match(/\sclass\s*=\s*"([^"]*)"/i);
    let newAttrs: string;
    if (classMatch) {
      const merged = `${classMatch[1]} scroll-mt-28`.trim();
      newAttrs = existing.replace(/\sclass\s*=\s*"[^"]*"/i, ` class="${merged}"`);
    } else {
      newAttrs = `${existing} class="scroll-mt-28"`;
    }
    return `<h${lvl} id="${item.id}"${newAttrs}>`;
  });
}

/**
 * Split an HTML body before the 2nd top-level <h2> so we can inject an
 * inline CTA mid-article. Mirrors splitBodyForCTA() for Markdown.
 */
export function splitHtmlBodyForCTA(body: string): [string, string] {
  if (!body) return ["", ""];
  const re = /<h2(?:\s[^>]*)?>/gi;
  const indices: number[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    indices.push(m.index);
    if (indices.length >= 2) break;
  }
  if (indices.length >= 2) {
    const splitAt = indices[1];
    return [body.slice(0, splitAt), body.slice(splitAt)];
  }
  const half = Math.floor(body.length / 2);
  const closeP = body.indexOf("</p>", half);
  if (closeP > 0) {
    const cut = closeP + 4;
    return [body.slice(0, cut), body.slice(cut)];
  }
  return [body, ""];
}

/**
 * Extract H2/H3 headings from a markdown body and assign unique slug IDs
 * matching the IDs we inject into rendered <h2>/<h3> via ReactMarkdown.
 */
export function extractHeadings(body: string): TocItem[] {
  if (!body) return [];
  const slugger = new GithubSlugger();
  const items: TocItem[] = [];
  const lines = body.split("\n");
  let inFence = false;
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^```/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const m = /^(#{2,3})\s+(.+?)\s*#*\s*$/.exec(line);
    if (!m) continue;
    const level = m[1].length as 2 | 3;
    // Strip simple markdown emphasis from heading text
    const text = m[2]
      .replace(/\*\*?(.+?)\*\*?/g, "$1")
      .replace(/`([^`]+)`/g, "$1")
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
      .trim();
    items.push({ id: slugger.slug(text), text, level });
  }
  return items;
}

/**
 * Build a queue of slugs by visible heading text, in order. ReactMarkdown
 * renders headings in source order, so each call to nextSlug(text) returns
 * the matching id for that occurrence.
 */
export function createSlugQueue(items: TocItem[]) {
  const queue: string[] = items.map((i) => i.id);
  let cursor = 0;
  return {
    next() {
      return queue[cursor++];
    },
    reset() {
      cursor = 0;
    },
  };
}